import { useMemo } from 'react';
import { useMe } from '../organisation/useMe';
import { useQueryDashboards } from '../dashboard/dashboardActions';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { getDashboardPath, navbarItems, NavbarItemType, NavbarSubItemType } from './helper';
import { dashboardToPath } from '../dashboard/dashboardHelpers';

const useVisibleNavbarItems = () => {
  const {
    roles: userRoles,
    permissions: userPermissions,
    features: organisationFeatures,
    currentOrganisation,
  } = useMe();

  const checkSubItems = (item: NavbarItemType | NavbarSubItemType): item is NavbarItemType =>
    'subItems' in item && item.subItems !== undefined && item.subItems.length > 0;

  const filterByRole = (item: NavbarItemType | NavbarSubItemType): boolean => {
    let itemRoles: string[] = [];
    if (Array.isArray(item.role)) {
      itemRoles = item.role;
    } else if (item.role) {
      itemRoles = [item.role];
    }
    const canShowItem = !item.role || itemRoles.some((rule) => userRoles?.includes(rule));

    const hasSubItems = checkSubItems(item);
    const canShowSubItem = !hasSubItems || (hasSubItems && item.subItems!.some(filterByRole));

    return canShowItem && canShowSubItem;
  };

  const filterByPermission = (item: NavbarItemType | NavbarSubItemType): boolean => {
    let itemPermissions: string[] = [];
    let rule: 'all' | 'any' = 'any';
    if (Array.isArray(item.permission)) {
      itemPermissions = item.permission;
    } else if (typeof item.permission === 'object') {
      itemPermissions = item.permission.permissions;
      rule = item.permission.rule;
    } else if (item.permission) {
      itemPermissions = [item.permission];
    }

    const canShowItem =
      itemPermissions.length === 0 ||
      (rule === 'any'
        ? itemPermissions.some((permission) => userPermissions.includes(permission))
        : itemPermissions.every((permission) => userPermissions.includes(permission)));

    const hasSubItems = checkSubItems(item);
    const canShowSubItem = !hasSubItems || (hasSubItems && item.subItems!.some(filterByPermission));

    return canShowItem && canShowSubItem;
  };

  const filterByFeature = (item: NavbarItemType | NavbarSubItemType): boolean => {
    let itemFeatures: ORGANISATION_FEATURE[] = [];
    let rule: 'all' | 'any' = 'any';
    if (Array.isArray(item.feature)) {
      itemFeatures = item.feature;
    } else if (typeof item.feature === 'object') {
      itemFeatures = item.feature.features;
      rule = item.feature.rule;
    } else if (item.feature) {
      itemFeatures = [item.feature];
    }

    const canShowItem =
      itemFeatures.length === 0 ||
      (rule === 'any'
        ? itemFeatures.some((feature) => isFeatureEnabled(feature, organisationFeatures))
        : itemFeatures.every((feature) => isFeatureEnabled(feature, organisationFeatures)));

    const hasSubItems = checkSubItems(item);
    const canShowSubItem = !hasSubItems || (hasSubItems && item.subItems!.some(filterByFeature));

    return canShowItem && canShowSubItem;
  };

  const filterByOrganisation = (item: NavbarItemType | NavbarSubItemType): boolean => {
    const canShowItem = !!currentOrganisation || !!item.organisationNotRequired;

    const hasSubItems = checkSubItems(item);
    const canShowSubItem = !hasSubItems || (hasSubItems && item.subItems!.some(filterByOrganisation));

    return canShowItem && canShowSubItem;
  };

  const { data: dashboards } = useQueryDashboards();

  const navBarItemsWithDashboards = useMemo(() => {
    if (dashboards?.length) {
      const copy = [...navbarItems];
      copy.find((item) => item.title === 'Dashboards')!.subItems = dashboards
        .filter((dashboard) => !dashboard.hidden)
        .map((dashboard) => ({
          title: dashboard.name,
          link: getDashboardPath(dashboardToPath(dashboard)),
        }));
      return copy;
    } else {
      return navbarItems;
    }
  }, [dashboards]);

  const visibleNavbarItems = useMemo(() => {
    return navBarItemsWithDashboards
      .filter(filterByOrganisation)
      .filter(filterByRole)
      .filter(filterByPermission)
      .filter(filterByFeature)
      .map((item) => {
        if (!item.subItems) {
          return item;
        }

        return {
          ...item,
          subItems: item.subItems
            .filter(filterByOrganisation)
            .filter(filterByRole)
            .filter(filterByPermission)
            .filter(filterByFeature),
        };
      });
  }, [userRoles, userPermissions, organisationFeatures, currentOrganisation, navBarItemsWithDashboards]);

  return { visibleNavbarItems };
};

export default useVisibleNavbarItems;
